// Angular imports
import { Component, Input, OnInit, ViewChild } from '@angular/core';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// 3rd party
import dxPopover from 'devextreme/ui/popover';
import { cloneDeep } from 'lodash';

// Service imports
import { SecurityInfoResultDto } from '../_models/security-info-result-dto';

// Constant imports
import { Constants } from '../../_constants/constants';

// Model imports
import { SecurityInfoService } from '../_services/security-info.service';

@Component({
    selector: 'app-security-info',
    templateUrl: './security-info.component.html',
    styleUrls: ['./security-info.component.scss']
})
export class SecurityInfoComponent implements OnInit {
    @ViewChild('securityIcon', { static: false }) securityIcon: HTMLDivElement;

    @Input() iconColor = 'dx-theme-accent-as-text-color';
    @Input() securityIndicator: 'LOCK' | 'KEY' | 'SHIELD' = 'LOCK';
    @Input() accessCode: string;
    @Input() apiUrl: string;
    @Input() apiAction: 'GET' | 'POST' | 'PUT' | 'DELETE';
    @Input() configName: string;
    @Input() configCategory: string;
    @Input() cssClass = '';

    securityInfoVisible = false;
    securityInfoPinned = false;
    securityInfo: SecurityInfoResultDto;
    securityInfoGroup: { name: string; htmlText: string }[];
    iconClass: string;

    private _scheduleTimer: any;

    constructor(private readonly securityInfoService: SecurityInfoService) {}

    ngOnInit(): void {
        /* As per ANG-1964, Replacing LOCK/Key icon by SHIELD icon
        this.iconClass = IconConstants.SHIELD;
        if (this.securityIndicator === 'KEY') {
            this.iconClass = IconConstants.KEY;
        } else if (this.securityIndicator === 'LOCK') {
            this.iconClass = IconConstants.LOCK;
        }
        */
        this.iconClass = IconConstants.SHIELD;
    }

    startSchedule(): void {
        this.stopSchedule();
        this._scheduleTimer = setTimeout(this.showSecurityInfo.bind(this), Constants.defaultSecurityInfoDelay);
    }

    stopSchedule(): void {
        clearTimeout(this._scheduleTimer);
        this.securityInfoVisible = this.securityInfoPinned;
    }

    ignoreSchedule(e: any): void {
        this.securityInfoPinned = true;
        this.setCurrentSecurityInfoIndicator(e.component);
    }

    restartSchedule(): void {
        this.securityInfoPinned = false;
    }

    private setCurrentSecurityInfoIndicator(instance: dxPopover): void {
        this.hideOtherSecurityInfoIndicators();
        this.securityInfoService.indicatorInstance = instance;
    }

    private hideOtherSecurityInfoIndicators(): void {
        if (!!this.securityInfoService.indicatorInstance) {
            this.securityInfoService.indicatorInstance.hide();
            this.securityInfoService.indicatorInstance = null;
        }
    }

    private showSecurityInfo(): void {
        if (!!this.securityInfo) {
            this.hideOtherSecurityInfoIndicators();
            this.securityInfoVisible = Utils.notNullOrEmpty(this.securityInfoGroup);
        } else {
            if (!!this.accessCode) {
                this.populateAccessSecurityInfo();
            } else if (!!this.configName && !!this.configCategory) {
                this.populateConfigSecurityInfo();
            } else if (!!this.apiUrl && !!this.apiAction) {
                this.populateApiUriSecurityInfo();
            }
        }
    }

    private populateAccessSecurityInfo(): void {
        this.securityInfoService.getAccessCodeSecurityInfo(this.accessCode).subscribe((result) => {
            this.securityInfo = cloneDeep(result);
            this.buildSecurityInfoGroup();
        });
    }

    private populateConfigSecurityInfo(): void {
        this.securityInfoService.getConfigSecurityInfo(this.configName, this.configCategory).subscribe((result) => {
            this.securityInfo = cloneDeep(result);
            this.buildSecurityInfoGroup();
        });
    }

    private populateApiUriSecurityInfo(): void {
        this.securityInfoService.getApiUriSecurityInfo(this.apiUrl, this.apiAction).subscribe((result) => {
            this.securityInfo = cloneDeep(result);
            this.buildSecurityInfoGroup();
        });
    }

    private buildSecurityInfoGroup(): void {
        this.securityInfoGroup = [];

        if (
            this.securityInfo.access.length > 0 &&
            (this.securityInfo.roles.length > 0 ||
                this.securityInfo.departments.length > 0 ||
                this.securityInfo.users.length > 0 ||
                this.securityInfo.credentials.length > 0)
        ) {
            this.securityInfo.access = this.securityInfo.access.filter((d) => Utils.notEqualsIgnoreCase(d, 'INTERNAL'));
            this.securityInfo.access = this.securityInfo.access.map((d) => `BOARD ${d}`);
        }
        if (this.securityInfo.access.length > 0) {
            this.securityInfoGroup.push({ name: 'Access', htmlText: this.securityInfo.access.join('<br />') });
        }
        if (this.securityInfo.roles.length > 0) {
            this.securityInfoGroup.push({ name: 'Roles', htmlText: this.securityInfo.roles.join('<br />') });
        }
        if (this.securityInfo.departments.length > 0) {
            this.securityInfoGroup.push({
                name: 'Departments',
                htmlText: this.securityInfo.departments.join('<br />')
            });
        }
        if (this.securityInfo.users.length > 0) {
            this.securityInfoGroup.push({ name: 'Users', htmlText: this.securityInfo.users.join('<br />') });
        }
        if (this.securityInfo.credentials.length > 0) {
            this.securityInfoGroup.push({
                name: 'Credentials',
                htmlText: this.securityInfo.credentials.join('<br />')
            });
        }
        this.showSecurityInfo();
    }
}
